import type { DayVacation, VacationRange, Zone } from '../types';
import { fetchSchoolYear, fromJSON, schoolYearsForCalendarYear } from './api';
import { getCached, setCached } from './cache';
import fallback from './fallback.json';

const FALLBACK: Record<string, VacationRange[]> = Object.fromEntries(
  Object.entries(fallback as Record<string, unknown>).map(([sy, data]) => [
    sy,
    fromJSON(data as Parameters<typeof fromJSON>[0]).filter((r) => /^Vacances\s/.test(r.description)),
  ]),
);

function keepVacations(ranges: VacationRange[]): VacationRange[] {
  return ranges.filter((r) => /^Vacances\s/.test(r.description));
}

export async function loadSchoolYear(schoolYear: string): Promise<VacationRange[]> {
  const cached = getCached(schoolYear);
  if (cached) return keepVacations(cached);
  try {
    const fresh = await fetchSchoolYear(schoolYear);
    if (fresh.length > 0) {
      setCached(schoolYear, fresh);
      return keepVacations(fresh);
    }
  } catch {
    /* fall through to bundled snapshot */
  }
  return FALLBACK[schoolYear] ?? [];
}

export async function loadCalendarYear(year: number): Promise<VacationRange[]> {
  const [prev, next] = schoolYearsForCalendarYear(year);
  const [a, b] = await Promise.all([loadSchoolYear(prev), loadSchoolYear(next)]);
  return [...a, ...b];
}

// Map of YYYY-MM-DD -> set of zones on vacation that day.
export function buildVacationDayMap(ranges: VacationRange[]): Map<string, DayVacation> {
  const out = new Map<string, DayVacation>();
  for (const range of ranges) {
    const start = new Date(`${range.start}T00:00:00`);
    const end = new Date(`${range.end}T00:00:00`);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = isoDate(d);
      let entry = out.get(key);
      if (!entry) {
        entry = { zones: new Set<Zone>() };
        out.set(key, entry);
      }
      for (const z of range.zones) entry.zones.add(z);
    }
  }
  return out;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
